import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import type { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";

import { useLogin } from "@/hooks/services/use-auth";
import { LoginSchema } from "@/schemas/auth";
import { useAuthStore } from "@/store/auth-store";

type LoginValues = z.infer<typeof LoginSchema>;

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("session") === "expired";
  const navigate = useNavigate();
  const location = useLocation();
  const savedCredentials = useAuthStore((state) => state.savedCredentials);
  const rememberMe = useAuthStore((state) => state.rememberMe);

  // Get the previous location if available
  const state = location.state;
  const from = state?.from?.pathname || "/";

  const login = useLogin({
    onSuccess: () => {
      // Navigate to the page user get back to visit or "/" route
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
    },
  });

  // Initialize form with React Hook Form and Zod validation
  const form = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      // Auto-fill username if saved credentials exist
      username: savedCredentials?.username || "",
      password: "",
      rememberMe,
    },
  });

  // Form submission handler
  const onSubmit = (data: LoginValues) => {
    // If rememberMe is checked, save the username for next time
    if (data.rememberMe) {
      useAuthStore.getState().saveCredentials(data.username);
    }

    login.mutate(data);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="relative w-full max-w-md rounded-lg bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-xl font-bold text-gray-900">Payment Portal !</h1>
          <p className="text-sm text-gray-600">
            Sign In to access your dashboard, settings and projects.
          </p>
        </div>

        {/* Session expired alert */}
        {sessionExpired && (
          <Alert className="mb-6" variant={"destructive"}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Session expired</AlertTitle>
            <AlertDescription>
              Your session has expired. Please log in again.
            </AlertDescription>
          </Alert>
        )}

        {/* Form with validation */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* User Name field */}
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>User Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter your user name"
                      className="border-gray-300 px-4 text-sm"
                      disabled={login.isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password field */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="border-gray-300 px-4 text-sm"
                        disabled={login.isPending}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500"
                        disabled={login.isPending}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remember me and forgot password */}
            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-y-0 space-x-1">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-4 w-4 border-blue-500 bg-blue-500 text-white"
                        disabled={login.isPending}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer text-sm font-normal text-gray-700">
                      Keep me signed in
                    </FormLabel>
                  </FormItem>
                )}
              />
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Forgot password?
              </Link>
            </div>

            {/* Sign in button with loading state */}
            <Button
              type="submit"
              className="w-full bg-blue-600 text-sm font-medium hover:bg-blue-700"
              disabled={login.isPending}
            >
              {login.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
        </Form>

        {/* Sign up link */}
        <div className="mt-6 text-center text-sm text-gray-600">
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-blue-600 hover:text-blue-700"
          >
            Sign Up
          </Link>
        </div>
      </div>
    </div>
  );
}
